/*
 *  Copyright (c) 2025 Comcast
 */

import * as React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import colors from 'components/utils/colors'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'
import { useTableDropdownMenu } from '@tiptap-ui/components/tiptap-ui/table-dropdown-menu'

// --- Primitives ---
import { Button, ButtonGroup } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Card, CardBody } from '@tiptap-ui/components/tiptap-ui-primitive/card'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@tiptap-ui/components/tiptap-ui-primitive/dropdown-menu'
import { InsertTablePopover } from '@tiptap-ui/components/tiptap-ui/table-dropdown-menu/insert-table-popover'
import { ChevronDownIcon } from '@tiptap-ui/components/tiptap-icons/chevron-down-icon'

const StyledButton = styled(Button)`
  &&&:hover:not([disabled]) {
    background: ${colors.bgPrimaryHover};
  }
`

const runAddRowAfter = (editor) => editor.chain().focus().addRowAfter().run()
const runAddColumnAfter = (editor) => editor.chain().focus().addColumnAfter().run()
const runDeleteRow = (editor) => editor.chain().focus().deleteRow().run()
const runDeleteColumn = (editor) => editor.chain().focus().deleteColumn().run()
const runDeleteTable = (editor) => editor.chain().focus().deleteTable().run()

const TABLE_ACTIONS = [
  { key: 'addRowAfter', label: 'Add row after', run: runAddRowAfter },
  { key: 'addColumnAfter', label: 'Add column after', run: runAddColumnAfter },
  { key: 'deleteRow', label: 'Delete row', run: runDeleteRow },
  { key: 'deleteColumn', label: 'Delete column', run: runDeleteColumn },
  { key: 'deleteTable', label: 'Delete table', run: runDeleteTable },
]
export const TableDropdownMenu = React.forwardRef((
  {
    editor: providedEditor,
    portal = false,
    ...buttonProps
  },
  ref,
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = React.useState(false)
  const { isVisible, isActive, canOpen, Icon, enabledMap } = useTableDropdownMenu({ editor })

  // This popover is used to configure the table settings before insertion
  const insertPopoverRef = React.useRef(null)

  const handleOpenChange = React.useCallback((open) => {
    if (!editor || !canOpen) {
      return
    }
    setIsOpen(open)
  }, [canOpen, editor])

  const handleAction = React.useCallback((action) => {
    if (!editor) {
      return
    }
    action.run(editor)
  }, [editor])

  const handleStartConfigure = React.useCallback(() => {
    if (!editor) {
      return
    }
    setIsOpen(false)
    insertPopoverRef.current?.open()
  }, [editor])

  if (!isVisible) {
    return null
  }

  return (
    <>
      <DropdownMenu modal open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            type='button'
            data-style='ghost'
            data-active-state={isActive ? 'on' : 'off'}
            role='button'
            tabIndex={-1}
            disabled={!canOpen}
            data-disabled={!canOpen}
            aria-label='Table actions'
            aria-pressed={isActive}
            tooltip='Table'
            {...buttonProps}
            ref={ref}
          >
            <Icon className='tiptap-button-icon' />
            <ChevronDownIcon className='tiptap-button-dropdown-small' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' portal={portal}>
          <Card>
            <CardBody>
              <ButtonGroup orientation='vertical'>
                <DropdownMenuItem asChild>
                  <StyledButton
                    type='button'
                    role='menuitem'
                    tabIndex={-1}
                    data-style='ghost'
                    disabled={!enabledMap.insertTable}
                    data-disabled={!enabledMap.insertTable}
                    aria-label='Insert table'
                    tooltip='Insert table'
                    onClick={handleStartConfigure}
                  >
                    <span className='tiptap-button-text'>Insert table…</span>
                  </StyledButton>
                </DropdownMenuItem>
                {TABLE_ACTIONS.map(action => {
                  const enabled = !!enabledMap[action.key]
                  return (
                    <DropdownMenuItem key={action.key} asChild>
                      <StyledButton
                        type='button'
                        role='menuitem'
                        tabIndex={-1}
                        data-style='ghost'
                        disabled={!enabled}
                        data-disabled={!enabled}
                        aria-label={action.label}
                        onClick={() => handleAction(action)}
                      >
                        <span className='tiptap-button-text'>{action.label}</span>
                      </StyledButton>
                    </DropdownMenuItem>
                  )
                })}
              </ButtonGroup>
            </CardBody>
          </Card>
        </DropdownMenuContent>
      </DropdownMenu>
      <InsertTablePopover
        ref={insertPopoverRef}
        editor={editor}
        disabled={!enabledMap.insertTable}
      />
    </>
  )
})

TableDropdownMenu.displayName = 'TableDropdownMenu'

TableDropdownMenu.propTypes = {
  editor: PropTypes.object,
  portal: PropTypes.bool,
}

export default TableDropdownMenu
